import React, { useEffect, useState } from 'react';

import Snackbar from 'components/DesignSystem/Snackbar';
import Section from 'components/Meteorology/Shared/ControlPanelSheet/Section';
import OptionRow from 'components/Meteorology/Shared/ControlPanelSheet/OptionRow';
import SheetFrame from 'components/Meteorology/Shared/ControlPanelSheet/DetailedView/SheetFrame';
import PreferencesSection from 'components/Meteorology/Shared/ControlPanelSheet/DetailedView/PreferencesSection';
import GroupSection from 'components/Meteorology/Shared/ControlPanelSheet/DetailedView/GroupSection';
import LocationSection from 'components/Meteorology/Shared/ControlPanelSheet/DetailedView/LocationSection';
import MultiGroupSection from 'components/Meteorology/Shared/ControlPanelSheet/DetailedView/MultiGroupSection';
import MultiLocationSection from 'components/Meteorology/Shared/ControlPanelSheet/DetailedView/MultiLocationSection';
import TimeFrequencySection from 'components/Meteorology/Shared/ControlPanelSheet/DetailedView/TimeFrequencySection';
import VariableSection from 'components/Meteorology/Shared/ControlPanelSheet/DetailedView/VariableSection';
import ForecastDateSection from 'components/Meteorology/Shared/ControlPanelSheet/DetailedView/ForecastDateSection';
import RoundsSection from 'components/Meteorology/Shared/ControlPanelSheet/DetailedView/RoundsSection';
import useDetailedViewMobile from 'hooks/Meteorology/Fields/TrackingForecast/DetailedView/Mobile/useDetailedViewMobile';

import PreferenceRow from './PreferenceRow';

type SheetSection =
  | 'preferences' | 'group' | 'location' | 'multigroup' | 'multilocation'
  | 'timefrequency' | 'model' | 'multimodel' | 'variable' | 'members'
  | 'forecastdate' | 'rounds' | '';

export default function ControlPanelSheet({
  open,
  dismiss,
}: {
  open: boolean;
  dismiss: () => void;
}) {
  const [openSection, setOpenSection] = useState<SheetSection>('variable');
  const [groupSearch, setGroupSearch] = useState('');
  const [locationSearch, setLocationSearch] = useState('');
  const [multiGroupSearch, setMultiGroupSearch] = useState('');
  const [multiLocationSearch, setMultiLocationSearch] = useState('');

  const {
    subpage,
    triggerRequest,
    clearSelection,
    savePreference,
    selectPreference,
    preferences,
    preferenceDeleteTrigger,
    groupedLocations,
    timeFrequency,
    models,
    variables,
    members,
    forecastDate,
    rounds,
  } = useDetailedViewMobile();

  useEffect(() => {
    if (open) setOpenSection('variable');
  }, [open]);

  const toggleSection = (section: SheetSection) => () => (
    setOpenSection((current) => (current === section ? '' : section))
  );

  const selectedMultiModelCount = models.models
    .filter((m) => models.isModelSelected(m.value)).length;

  // Ordem das seções replica exatamente o painel desktop por subpage
  // (ControlPanel/TrackingForecast/DetailedView/index.tsx).
  const groupSection = (
    <GroupSection
      groupedLocations={groupedLocations}
      search={groupSearch}
      onSearchChange={setGroupSearch}
      open={openSection === 'group'}
      onToggle={toggleSection('group')}
    />
  );

  const locationSection = (
    <LocationSection
      groupedLocations={groupedLocations}
      search={locationSearch}
      onSearchChange={setLocationSearch}
      open={openSection === 'location'}
      onToggle={toggleSection('location')}
    />
  );

  const timeFrequencySection = (
    <TimeFrequencySection
      timeFrequency={timeFrequency}
      open={openSection === 'timefrequency'}
      onToggle={toggleSection('timefrequency')}
    />
  );

  const modelSection = (
    <Section
      label="Modelo"
      valueText={models.selectedModel?.label}
      open={openSection === 'model'}
      onToggle={toggleSection('model')}
    >
      {models.models.length === 0 && (
        <Snackbar>Nenhum modelo disponível para a frequência temporal selecionada.</Snackbar>
      )}
      {models.models.map((model) => (
        <OptionRow
          key={model.id}
          label={model.label}
          selected={model.value === models.selectedModel?.value}
          onSelect={() => models.setModel(model.value)}
        />
      ))}
    </Section>
  );

  const variableSection = (
    <VariableSection
      variables={variables}
      open={openSection === 'variable'}
      onToggle={toggleSection('variable')}
    />
  );

  const forecastDateSection = (
    <ForecastDateSection
      forecastDate={forecastDate}
      open={openSection === 'forecastdate'}
      onToggle={toggleSection('forecastdate')}
    />
  );

  return (
    <SheetFrame
      open={open}
      dismiss={dismiss}
      className="tf-dv-control-panel-sheet"
      savePreference={savePreference}
      triggerRequest={triggerRequest}
      clearSelection={clearSelection}
    >
      <PreferencesSection
        preferences={preferences}
        renderRow={(preference) => (
          <PreferenceRow
            preference={preference}
            onSelect={() => { selectPreference(preference)(); dismiss(); }}
            onDelete={preferenceDeleteTrigger(preference.name)}
          />
        )}
        open={openSection === 'preferences'}
        onToggle={toggleSection('preferences')}
      />

      {subpage === 'locations' && (
        <>
          <MultiGroupSection
            groupedLocations={groupedLocations}
            search={multiGroupSearch}
            onSearchChange={setMultiGroupSearch}
            open={openSection === 'multigroup'}
            onToggle={toggleSection('multigroup')}
          />
          <MultiLocationSection
            groupedLocations={groupedLocations}
            search={multiLocationSearch}
            onSearchChange={setMultiLocationSearch}
            open={openSection === 'multilocation'}
            onToggle={toggleSection('multilocation')}
          />
          {timeFrequencySection}
          {modelSection}
          {variableSection}
          {forecastDateSection}
        </>
      )}

      {subpage === 'members' && (
        <>
          {groupSection}
          {locationSection}
          {timeFrequencySection}
          {modelSection}

          <Section
            label="Membros"
            valueText={members.selectedMembers.length ? `${members.selectedMembers.length} selecionados` : undefined}
            open={openSection === 'members'}
            onToggle={toggleSection('members')}
          >
            {members.members.length === 0 && (
              <Snackbar>Nenhum membro disponível para os parâmetros selecionados.</Snackbar>
            )}
            {members.members.map((member) => (
              <OptionRow
                key={member.id}
                label={member.label}
                selected={members.selectedMembers.includes(member.id)}
                onSelect={() => {
                  const isSelected = members.selectedMembers.includes(member.id);
                  members.setSelectedMembers(isSelected
                    ? members.selectedMembers.filter((id) => id !== member.id)
                    : [...members.selectedMembers, member.id]);
                }}
                selectionType="multiple"
              />
            ))}
          </Section>

          {variableSection}
          {forecastDateSection}
        </>
      )}

      {subpage === 'models' && (
        <>
          {groupSection}
          {locationSection}
          {timeFrequencySection}
          {forecastDateSection}

          <Section
            label="Modelos"
            valueText={selectedMultiModelCount
              ? `${selectedMultiModelCount} selecionados` : undefined}
            open={openSection === 'multimodel'}
            onToggle={toggleSection('multimodel')}
          >
            {models.models.length === 0 && (
              <Snackbar>
                Nenhum modelo disponível para a frequência temporal selecionada.
              </Snackbar>
            )}
            {models.models.map((model) => (
              <OptionRow
                key={model.id}
                label={model.label}
                selected={models.isModelSelected(model.value)}
                onSelect={() => models.toggleMultiModel(model.value)}
                selectionType="multiple"
              />
            ))}
          </Section>

          {variableSection}
        </>
      )}

      {subpage === 'rounds' && (
        <>
          {groupSection}
          {locationSection}
          {timeFrequencySection}
          {modelSection}
          {variableSection}
          <RoundsSection
            rounds={rounds}
            open={openSection === 'rounds'}
            onToggle={toggleSection('rounds')}
          />
        </>
      )}
    </SheetFrame>
  );
}
